import React, { createContext, useMemo, useContext, useRef } from 'react';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import settingsParams, {
  TextAlignment,
  LayoutType,
} from '../../settingsParams';
import stylesParams from '../../stylesParams';
import { useLayoutType } from '../../../../hooks/useLayoutType';

export interface RCVSettingsValue {
  layout: LayoutType;
  textAlignment: TextAlignment;
  isMobile: boolean;
  isEditor: boolean;
  customStyles: Record<
    string,
    {
      fontFamily?: string;
      fontSize?: string;
      fontWeight?: string;
      fontStyle?: string;
      textDecoration?: string;
    }
  >;
  palette: { actionColor?: string; textColor?: string };
}

const defaultRCVSettings: RCVSettingsValue = {
  layout: LayoutType.Collapse,
  textAlignment: 'left' as TextAlignment,
  isMobile: false,
  isEditor: false,
  customStyles: {},
  palette: {},
};

export const RCVSettingsContext =
  createContext<RCVSettingsValue>(defaultRCVSettings);

function buildCustomStyles(answerFont: {
  family?: string;
  size?: number;
  style?: { bold?: boolean; italic?: boolean; underline?: boolean };
}): RCVSettingsValue['customStyles'] {
  const customAnswerStyle = {
    fontFamily: answerFont?.family,
    fontSize: answerFont?.size != null ? `${answerFont.size}px` : undefined,
    ...(answerFont?.style?.bold && { fontWeight: 'bold' as const }),
    ...(answerFont?.style?.italic && { fontStyle: 'italic' as const }),
    ...(answerFont?.style?.underline && {
      textDecoration: 'underline' as const,
    }),
  };
  const elements = [
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'span',
    'link',
    'hashtag',
    'quote',
    'audio',
  ];
  return elements.reduce<RCVSettingsValue['customStyles']>((acc, el) => {
    acc[el] = customAnswerStyle;
    return acc;
  }, {});
}

interface RCVSettingsProviderProps {
  children: React.ReactNode;
  isEditor3?: boolean;
}

export function RCVSettingsProvider({
  children,
  isEditor3,
}: RCVSettingsProviderProps) {
  const settings = useSettings();
  const styles = useStyles();
  const environment = useEnvironment();
  const {
    layout: { layoutValue: layout },
  } = useLayoutType(isEditor3);

  const textAlignment =
    (settings.get(settingsParams.textAlignment) as TextAlignment | undefined) ??
    ('left' as TextAlignment);
  const { isMobile, isEditor } = environment;
  const answerFont = styles.get(stylesParams.answerTextFont);
  const answerTextColor = styles.get(stylesParams.answerTextColor);
  const answersLinksColor = styles.get(stylesParams.answersLinksColor);

  const actionColor =
    answersLinksColor?.value ?? answersLinksColor?.name ?? undefined;
  const textColor =
    answerTextColor?.value ?? answerTextColor?.name ?? undefined;

  const prevRef = useRef<RCVSettingsValue>(defaultRCVSettings);
  const value = useMemo<RCVSettingsValue>(() => {
    const customStyles = buildCustomStyles(answerFont ?? {});
    const next: RCVSettingsValue = {
      layout,
      textAlignment,
      isMobile,
      isEditor,
      customStyles,
      palette: { actionColor, textColor },
    };
    const prev = prevRef.current;
    const prevStyle = prev.customStyles?.p;
    const nextStyle = next.customStyles?.p;
    if (
      prev.layout === next.layout &&
      prev.textAlignment === next.textAlignment &&
      prev.isMobile === next.isMobile &&
      prev.isEditor === next.isEditor &&
      prev.palette.actionColor === next.palette.actionColor &&
      prev.palette.textColor === next.palette.textColor &&
      prevStyle?.fontFamily === nextStyle?.fontFamily &&
      prevStyle?.fontSize === nextStyle?.fontSize &&
      prevStyle?.fontWeight === nextStyle?.fontWeight &&
      prevStyle?.fontStyle === nextStyle?.fontStyle &&
      prevStyle?.textDecoration === nextStyle?.textDecoration
    ) {
      return prev;
    }
    prevRef.current = next;
    return next;
    // Depend on answerFont fields only so we don't recompute when styles.get() returns a new object reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layout,
    textAlignment,
    isMobile,
    isEditor,
    actionColor,
    textColor,
    answerFont?.family,
    answerFont?.size,
    answerFont?.style?.bold,
    answerFont?.style?.italic,
    answerFont?.style?.underline,
  ]);

  return (
    <RCVSettingsContext.Provider value={value}>
      {children}
    </RCVSettingsContext.Provider>
  );
}

export function useRCVSettings(): RCVSettingsValue {
  return useContext(RCVSettingsContext);
}

// Stable context for instance/metaSiteId so RCV doesn't re-render when AppContext updates
export interface RCVInstanceValue {
  metaSiteId: string | undefined;
  instance: string;
}

const defaultRCVInstance: RCVInstanceValue = {
  metaSiteId: undefined,
  instance: '',
};

export const RCVInstanceContext =
  createContext<RCVInstanceValue>(defaultRCVInstance);

interface RCVInstanceProviderProps {
  children: React.ReactNode;
  metaSiteId: string | undefined;
  instance: string;
}

export function RCVInstanceProvider({
  children,
  metaSiteId,
  instance,
}: RCVInstanceProviderProps) {
  const value = useMemo<RCVInstanceValue>(
    () => ({ metaSiteId, instance }),
    [metaSiteId, instance],
  );
  return (
    <RCVInstanceContext.Provider value={value}>
      {children}
    </RCVInstanceContext.Provider>
  );
}

export function useRCVInstance(): RCVInstanceValue {
  return useContext(RCVInstanceContext);
}
